import { useState } from 'react';
import { Badge } from './ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { ChevronDown, Shield, ShieldCheck } from 'lucide-react';
import { TASK_STATUSES, getStatusConfig, getUserAllowedStatuses, getManagerAllowedStatuses } from '../lib/taskStatus';

const TaskStatusBadge = ({ status, requiresApproval, isManager, onStatusChange, compact = false }) => {
  const config = getStatusConfig(status);
  const allowedStatuses = isManager 
    ? getManagerAllowedStatuses() 
    : getUserAllowedStatuses(status, requiresApproval);

  if (!onStatusChange) {
    return (
      <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 ${config.color}`} data-testid={`status-badge-${status}`}>
        <div className={`w-1.5 h-1.5 rounded-full mr-1 ${config.dotColor}`} />
        {!compact && config.label}
      </Badge>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="focus:outline-none" data-testid={`status-trigger-${status}`}>
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0.5 cursor-pointer hover:opacity-80 transition-opacity ${config.color}`}>
            <div className={`w-1.5 h-1.5 rounded-full mr-1 ${config.dotColor}`} />
            {!compact && config.label}
            <ChevronDown className="w-3 h-3 ml-0.5 opacity-50" />
          </Badge>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-48 dark:bg-slate-800 dark:border-slate-700" data-testid="status-dropdown">
        <div className="px-2 py-1.5 text-[10px] text-muted-foreground font-medium uppercase tracking-wider">
          Change Status
        </div>
        <DropdownMenuSeparator />
        {allowedStatuses.map(s => (
          <DropdownMenuItem
            key={s.value}
            onClick={() => onStatusChange(s.value)}
            className={`text-xs ${s.value === status ? 'bg-accent' : ''}`}
            data-testid={`status-option-${s.value}`}
          >
            <div className={`w-2 h-2 rounded-full mr-2 ${s.dotColor}`} />
            {s.label}
            {s.value === status && <span className="ml-auto text-[10px] text-muted-foreground">Current</span>}
          </DropdownMenuItem>
        ))}
        {requiresApproval && !isManager && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-[10px] text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Requires manager approval
            </div>
          </>
        )}
        {isManager && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-1.5 text-[10px] text-emerald-600 dark:text-emerald-400 flex items-center gap-1">
              <ShieldCheck className="w-3 h-3" />
              Manager access
            </div>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TaskStatusBadge;
